import { Button, Group, Modal, MultiSelect, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ZendeskConnection, ZendeskSourceCreate, createZendeskSource, getDatasetZendeskConnections } from '@mygpt/app';
import { ZendeskOAuth } from '../../ZendeskOAuth';

interface Props {
  datasetId: string;
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface FormValues {
  connection_mode: 'existing' | 'new' | 'oauth';
  connection_id: string;
  subdomain: string;
  admin_email: string;
  api_token: string;
  friendly_name: string;
  import_since: string;
  import_statuses: string[];
  import_limit: number;
}

const TICKET_STATUSES = ['solved', 'closed', 'pending', 'open', 'hold', 'new'];

const normalizeSubdomain = (raw: string) =>
  (raw || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .replace(/\.zendesk\.com$/, '');

// 把一个 Zendesk 接続挂为知识库数据源（接続与 bot 侧連携アプリ共用，可复用）
export const AddSourceModal = ({ datasetId, opened, onClose, onCreated }: Props) => {
  const { t } = useTranslation();
  const [connections, setConnections] = useState<ZendeskConnection[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      connection_mode: 'new',
      connection_id: '',
      subdomain: '',
      admin_email: '',
      api_token: '',
      friendly_name: '',
      import_since: '',
      import_statuses: ['solved', 'closed'],
      import_limit: 200,
    },
    validate: {
      connection_id: (value, values) =>
        values.connection_mode === 'oauth' || (values.connection_mode === 'existing' && !value)
          ? t('publish.zendesk.validation.connection_required')
          : null,
      subdomain: (value, values) => {
        if (values.connection_mode !== 'new') return null;
        const s = normalizeSubdomain(value);
        if (!s) return t('publish.zendesk.validation.subdomain_required');
        if (!/^[a-z0-9][a-z0-9-]*$/.test(s)) return t('publish.zendesk.validation.subdomain_format');
        return null;
      },
      admin_email: (value, values) => {
        if (values.connection_mode !== 'new') return null;
        if (!value) return t('publish.zendesk.validation.admin_email_required');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return t('publish.zendesk.validation.admin_email_format');
        return null;
      },
      api_token: (value, values) =>
        values.connection_mode === 'new' && !value ? t('publish.zendesk.validation.api_token_required') : null,
      import_statuses: (value) => (value.length === 0 ? t('dataset.zendesk.validation.statuses_required') : null),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.reset();
    getDatasetZendeskConnections(datasetId)
      .then(({ data }) => {
        const list = data.connections || [];
        setConnections(list);
        if (list.length > 0) {
          form.setFieldValue('connection_mode', 'existing');
          form.setFieldValue('connection_id', list[0].id);
        }
      })
      .catch(() => setConnections([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, datasetId]);

  const handleSubmit = async (values: FormValues) => {
    try {
      setSubmitting(true);
      const body: ZendeskSourceCreate = {
        import_since: values.import_since ? new Date(values.import_since).toISOString() : null,
        import_statuses: values.import_statuses,
        import_limit: Math.max(0, Number(values.import_limit) || 0),
        ...(values.connection_mode === 'existing'
          ? { connection_id: values.connection_id }
          : {
              subdomain: normalizeSubdomain(values.subdomain),
              admin_email: values.admin_email.trim(),
              api_token: values.api_token.trim(),
              friendly_name: values.friendly_name.trim() || undefined,
            }),
      };
      await createZendeskSource(datasetId, body);
      showNotification({ color: 'green', message: t('dataset.zendesk.create_success') });
      onCreated();
    } catch (error: any) {
      const detail = error?.detail ?? error?.response?.data?.detail;
      showNotification({
        color: 'red',
        message: (typeof detail === 'string' && detail) || error?.errorMsg || t('errors.common'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('dataset.zendesk.add_source')} size="lg" centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="sm" c="gray.6">
            {t('dataset.zendesk.add_description')}
          </Text>
          <Select
            label={t('publish.zendesk.connection_mode')}
            allowDeselect={false}
            data={[
              { value: 'existing', label: t('publish.zendesk.use_existing_connection') },
              { value: 'new', label: t('publish.zendesk.new_connection') },
              { value: 'oauth', label: t('zendesk_oauth.new_connection') },
            ]}
            {...form.getInputProps('connection_mode')}
          />
          {form.values.connection_mode === 'existing' ? (
            <>
              <Select
                label={t('publish.zendesk.connection')}
                placeholder={t('publish.zendesk.connection_placeholder')}
                nothingFoundMessage={t('publish.zendesk.no_connections')}
                data={connections.map((c) => ({
                  value: c.id,
                  label: `${c.subdomain}.zendesk.com（${c.friendly_name || c.admin_email}）`,
                }))}
                {...form.getInputProps('connection_id')}
              />
              <ZendeskOAuth
                key={form.values.connection_id}
                connectionId={form.values.connection_id || undefined}
                subdomain={connections.find((c) => c.id === form.values.connection_id)?.subdomain || ''}
                authentication={connections.find((c) => c.id === form.values.connection_id)?.authentication}
                onAuthorized={() => {
                  return getDatasetZendeskConnections(datasetId).then(({ data }) =>
                    setConnections(data.connections || [])
                  );
                }}
                onCredentialsSaved={() => {
                  return getDatasetZendeskConnections(datasetId).then(({ data }) =>
                    setConnections(data.connections || [])
                  );
                }}
              />
            </>
          ) : form.values.connection_mode === 'oauth' ? (
            <>
              <TextInput label={t('publish.zendesk.subdomain')} {...form.getInputProps('subdomain')} />
              <TextInput label={t('publish.zendesk.friendly_name')} {...form.getInputProps('friendly_name')} />
              <ZendeskOAuth
                subdomain={normalizeSubdomain(form.values.subdomain)}
                friendlyName={form.values.friendly_name}
                onAuthorized={async (id) => {
                  const { data } = await getDatasetZendeskConnections(datasetId);
                  setConnections(data.connections || []);
                  form.setFieldValue('connection_id', id);
                  form.setFieldValue('connection_mode', 'existing');
                }}
              />
            </>
          ) : (
            <>
              <TextInput
                label={t('publish.zendesk.subdomain')}
                placeholder="your-subdomain"
                description={t('publish.zendesk.subdomain_description')}
                withAsterisk
                {...form.getInputProps('subdomain')}
              />
              <TextInput
                label={t('publish.zendesk.admin_email')}
                placeholder="agent@example.com"
                withAsterisk
                {...form.getInputProps('admin_email')}
              />
              <TextInput
                type="password"
                label={t('publish.zendesk.api_token')}
                description={t('publish.zendesk.api_token_description')}
                withAsterisk
                {...form.getInputProps('api_token')}
              />
              <TextInput label={t('publish.zendesk.friendly_name')} {...form.getInputProps('friendly_name')} />
            </>
          )}

          <Text size="sm" fw={600} mt="xs">
            {t('dataset.zendesk.import_settings')}
          </Text>
          <TextInput
            type="date"
            label={t('dataset.zendesk.import_since')}
            description={t('dataset.zendesk.import_since_description')}
            {...form.getInputProps('import_since')}
          />
          <MultiSelect
            label={t('dataset.zendesk.import_statuses')}
            description={t('dataset.zendesk.import_statuses_description')}
            data={TICKET_STATUSES.map((s) => ({ value: s, label: s }))}
            {...form.getInputProps('import_statuses')}
          />
          <NumberInput
            label={t('dataset.zendesk.import_limit')}
            description={t('dataset.zendesk.import_limit_description')}
            min={0}
            step={100}
            {...form.getInputProps('import_limit')}
          />
          <Text size="xs" c="gray.6">
            {t('dataset.zendesk.publish_rule_note')}
          </Text>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              {t('alert_cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {t('dataset.zendesk.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
